// src/components/WeightChart.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from "recharts";
import { useApp } from "../App";
import {
  getWeightEntries,
  addWeightEntry, updateWeightEntry, deleteWeightEntry,
} from "../services/firebase";
import { sevenDayMovingAverage } from "../utils/movingAverage";

const today = () => new Date().toISOString().slice(0, 10);
const fmt   = (d) => {
  const [,m,day] = d.split("-");
  return `${parseInt(m)}/${parseInt(day)}`;
};

export default function WeightChart() {
  const { user, profile } = useApp();
  const [entries,  setEntries]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  // Form
  const [weightInput, setWeightInput] = useState("");
  const [dateInput,   setDateInput]   = useState(today());
  const [editEntry,   setEditEntry]   = useState(null);   // { id, weight, date }

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error: err } = await getWeightEntries(user.uid, 90);
    if (!err) setEntries(data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Pre-fill form if today already has an entry
  useEffect(() => {
    const existing = entries.find((e) => e.date === dateInput);
    if (existing) {
      setEditEntry(existing);
      setWeightInput(String(existing.weight));
    } else {
      setEditEntry(null);
      setWeightInput("");
    }
  }, [dateInput, entries]);

  const handleSave = async () => {
    const w = parseFloat(weightInput);
    if (!w || w < 50 || w > 800) { setError("Enter a valid weight (50–800 lbs)."); return; }
    setError(""); setSaving(true);

    if (editEntry) {
      await updateWeightEntry(user.uid, editEntry.id, { weight: w });
    } else {
      await addWeightEntry(user.uid, { date: dateInput, weight: w });
    }

    await load();
    setSaving(false);
    setWeightInput("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this weight entry?")) return;
    await deleteWeightEntry(user.uid, id);
    await load();
  };

  // Build chart data — merge raw + SMA
  const chartData = buildChartData(entries, profile?.weight_goal);

  // Weekly summary
  const { thisWeekAvg, lastWeekAvg } = weekSummary(entries);

  // Current progress
  const latest     = entries.length > 0 ? entries[entries.length - 1].weight : profile?.weight_start;
  const lost       = profile ? (profile.weight_start - latest).toFixed(1) : 0;
  const toGoal     = profile ? (latest - profile.weight_goal).toFixed(1) : 0;
  const pctDone    = profile && (profile.weight_start - profile.weight_goal) > 0
    ? Math.min(100, Math.round(((profile.weight_start - latest) / (profile.weight_start - profile.weight_goal)) * 100))
    : 0;

  return (
    <div className="space-y-5 animate-slide-up">
      <h1 className="section-title">Weight Tracker</h1>

      {/* ── Log weight form ─────────────────────────────────────────── */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-200 mb-4">
          {editEntry ? "✏️ Update Entry" : "Log Today's Weight"}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label">Date</label>
            <input type="date" className="input" value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
              max={today()} />
          </div>
          <div>
            <label className="label">Weight (lbs)</label>
            <input type="number" className="input" placeholder="185.0" step="0.1"
              value={weightInput} onChange={(e) => setWeightInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()} />
          </div>
        </div>
        {error && <p className="text-red-400 text-xs mb-2">{error}</p>}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving || !weightInput} className="btn-primary flex-1">
            {saving ? "Saving…" : editEntry ? "Update" : "Log Weight"}
          </button>
          {editEntry && (
            <button onClick={() => { setEditEntry(null); setWeightInput(""); }}
              className="btn-ghost">Cancel</button>
          )}
        </div>
        {editEntry && (
          <p className="text-xs text-slate-500 mt-2 text-center">
            An entry already exists for this date — saving will update it.
          </p>
        )}
      </div>

      {/* ── Progress summary ────────────────────────────────────────── */}
      {profile && (
        <div className="card">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="stat-number text-slate-100 text-3xl">{latest}</div>
              <div className="text-xs text-slate-500 mt-1">Current (lbs)</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-emerald-400 text-3xl">−{lost}</div>
              <div className="text-xs text-slate-500 mt-1">Lost so far</div>
            </div>
            <div className="text-center">
              <div className="stat-number text-slate-300 text-3xl">{toGoal}</div>
              <div className="text-xs text-slate-500 mt-1">To goal (lbs)</div>
            </div>
          </div>
          <div className="progress-track h-2 mb-1">
            <div className="progress-fill h-2 bg-emerald-500" style={{ width: `${pctDone}%` }} />
          </div>
          <div className="text-xs text-slate-500">{pctDone}% of goal complete</div>
        </div>
      )}

      {/* ── Chart ───────────────────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-slate-200">Weight Trend (90 days)</div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-0.5 bg-[#ff7c0a] inline-block rounded" />
              7-day avg
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-slate-600 inline-block" />
              Daily
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-px bg-emerald-500 border-dashed border-t border-emerald-500 inline-block" />
              Goal
            </span>
          </div>
        </div>

        {loading ? (
          <div className="h-56 flex items-center justify-center">
            <div className="spinner" />
          </div>
        ) : entries.length < 2 ? (
          <div className="h-56 flex flex-col items-center justify-center text-slate-500">
            <span className="text-3xl mb-2">📊</span>
            <p className="text-sm">Log at least 2 entries to see your trend chart.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2a3c" />
              <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fill: "#64748b", fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${v}`}
              />
              <Tooltip content={<WeightTooltip />} />
              {profile?.weight_goal && (
                <ReferenceLine y={profile.weight_goal} stroke="#10b981" strokeDasharray="4 4"
                  label={{ value: `Goal ${profile.weight_goal}`, fill: "#10b981", fontSize: 10 }} />
              )}
              {/* Raw daily — subtle */}
              <Line
                type="monotone" dataKey="raw" stroke="#334155" strokeWidth={0}
                dot={<RawDot totalPoints={chartData.length} />} activeDot={{ r: 4, fill: "#94a3b8" }} connectNulls={false}
              />
              {/* 7-day SMA — primary */}
              <Line
                type="monotone" dataKey="sma" stroke="#ff7c0a" strokeWidth={2.5}
                dot={false} activeDot={{ r: 5, fill: "#ff7c0a" }} connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Weekly summary ───────────────────────────────────────────── */}
      {(thisWeekAvg || lastWeekAvg) && (
        <div className="card">
          <div className="text-sm font-semibold text-slate-200 mb-3">Weekly Summary</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">This week avg</div>
              <div className="text-xl font-bold text-slate-100"
                   style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {thisWeekAvg ? `${thisWeekAvg.toFixed(1)} lbs` : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Last week avg</div>
              <div className="text-xl font-bold text-slate-100"
                   style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                {lastWeekAvg ? `${lastWeekAvg.toFixed(1)} lbs` : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Week change</div>
              {thisWeekAvg && lastWeekAvg ? (
                <div className={`text-xl font-bold ${thisWeekAvg < lastWeekAvg ? "text-emerald-400" : "text-red-400"}`}
                     style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {thisWeekAvg < lastWeekAvg ? "−" : "+"}{Math.abs(thisWeekAvg - lastWeekAvg).toFixed(1)} lbs
                </div>
              ) : <div className="text-xl font-bold text-slate-500">—</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Entry list ───────────────────────────────────────────────── */}
      <div className="card">
        <div className="text-sm font-semibold text-slate-200 mb-3">
          Recent Entries ({entries.length})
        </div>
        {loading ? (
          <div className="space-y-2">
            {[1,2,3].map((i) => <div key={i} className="h-10 bg-[#111820] rounded-lg animate-pulse" />)}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <span className="text-2xl block mb-2">⚖️</span>
            No entries yet. Log your first weight above.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {[...entries].reverse().map((entry) => (
              <div key={entry.id}
                className="flex items-center justify-between px-3 py-2.5 bg-[#111820] rounded-lg">
                <div>
                  <span className="text-sm font-semibold text-slate-200">{entry.weight} lbs</span>
                  <span className="text-xs text-slate-500 ml-3">{entry.date}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setDateInput(entry.date); setWeightInput(String(entry.weight)); setEditEntry(entry); }}
                    className="text-xs text-slate-400 hover:text-[#ff7c0a] transition-colors px-2 py-1"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="text-xs text-slate-600 hover:text-red-400 transition-colors px-2 py-1"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Chart helpers ────────────────────────────────────────────────────────────

function buildChartData(entries, goalWeight) {
  if (!entries || entries.length === 0) return [];
  const sma = sevenDayMovingAverage(entries.map((e) => e.weight));
  return entries.map((e, i) => ({
    label: fmt(e.date),
    raw:   e.weight,
    sma:   sma[i] !== null ? parseFloat(sma[i].toFixed(1)) : null,
    diff:  i > 0 ? parseFloat((e.weight - entries[i - 1].weight).toFixed(1)) : null,
  }));
}

function weekSummary(entries) {
  const now  = new Date();
  const dow  = now.getDay();
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - dow);
  const lastStart = new Date(weekStart); lastStart.setDate(weekStart.getDate() - 7);

  const inRange = (e, start, end) => {
    const d = new Date(e.date + "T00:00:00");
    return d >= start && d < end;
  };

  const thisWeek = entries.filter((e) => inRange(e, weekStart, new Date(weekStart.getTime() + 7 * 86400000)));
  const lastWeek = entries.filter((e) => inRange(e, lastStart, weekStart));

  const avg = (arr) => arr.length ? arr.reduce((s, e) => s + e.weight, 0) / arr.length : null;

  return { thisWeekAvg: avg(thisWeek), lastWeekAvg: avg(lastWeek) };
}

const RawDot = (props) => {
  const { cx, cy, payload, index } = props;
  const diff = payload?.diff;
  const totalPoints = props.totalPoints || 90;
  // Show label every 7th point, or all if fewer than 15 entries
  const showLabel = diff !== null && diff !== 0 &&
    (totalPoints < 15 || index % 7 === 0);
  const color = diff < 0 ? "#10b981" : "#ef4444";
  const sign = diff > 0 ? "+" : "";
  return (
    <g>
      <circle cx={cx} cy={cy} r={2.5} fill="#334155" stroke="none" />
      {showLabel && (
        <text
          x={cx} y={cy - 10} textAnchor="middle"
          fill={color} fontSize={10} fontWeight="bold"
        >
          {sign}{diff}
        </text>
      )}
    </g>
  );
};

const WeightTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const raw = payload.find((p) => p.dataKey === "raw");
  const sma = payload.find((p) => p.dataKey === "sma");
  const diff = raw?.payload?.diff;
  return (
    <div className="bg-[#1a2332] border border-[#263347] rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-400 mb-1">{label}</div>
      {raw?.value && <div className="text-slate-300">Daily: <span className="font-bold">{raw.value} lbs</span></div>}
      {sma?.value && <div className="text-[#ff7c0a]">7-day avg: <span className="font-bold">{sma.value} lbs</span></div>}
      {diff !== null && diff !== undefined && (
        <div style={{ color: diff < 0 ? "#10b981" : diff > 0 ? "#ef4444" : "#64748b" }}>
          Change: <span className="font-bold">{diff > 0 ? "+" : ""}{diff} lbs</span>
        </div>
      )}
    </div>
  );
};
